import { c as _c } from "react/compiler-runtime";
import { Form, Formik } from 'formik';
import NextLink from 'next/link';
import { useContext, useEffect, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { PRODUCT_SLUGS, ProductSlug } from '../../../../../../domain/products';
import LINKS from '../../../configs/links';
import { isMultiFactorRequired } from '../../../typings/MultiFactor.interface';
import { Promotion, TrackingType } from '../../../typings/Tracking.interface';
import { capitalizeFirstLetter, stringifyQuery } from '../../../utils';
import AuthSensitivePageHead from '../../common/AuthSensitivePageHead';
import Alert from '../../common/base/Alert';
import Button from '../../common/base/Button';
import Divider from '../../common/base/Divider';
import Icon from '../../common/base/Icon';
import Link from '../../common/base/Link';
import Text from '../../common/base/Text';
import TextInput from '../../common/Form/Fields/TextInput';
import { Div } from '../../common/helpers/StyledUtils';
import { GlobalContext } from '../../contexts/GlobalContext';
import useLocalStorage from '../../hooks/useLocalStorage';
import { completeAuthRedirect } from '../../../utils/completeAuthRedirect';

// Main two-panel layout container
const StyledSignupContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 100vh;
  width: 100%;

  @media (max-width: ${({
  theme
}) => theme.breakpoints[1]}px) {
    grid-template-columns: 1fr;
  }
`;

// Left panel - white background with form
const StyledLeftPanel = styled.div(({
  theme
}) => css`
    background-color: ${theme.colors.surface.base.surface};
    box-shadow: ${theme.elevation[1]};
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    position: relative;
    z-index: 1;
  `);
const StyledTopBar = styled.div(({
  theme
}) => css`
    padding: ${theme.spacing(8)};
  `);
const StyledFormContainer = styled.div(({
  theme
}) => css`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: ${theme.spacing(8)};
  `);
const StyledFormContent = styled.div(({
  theme
}) => css`
    width: 100%;
    max-width: 408px;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(8)};
  `);
const StyledOAuthSection = styled.div(({
  theme
}) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};
  `);
const StyledFormSection = styled.div(({
  theme
}) => css`
    display: flex;
    flex-direction: column;
    padding-bottom: ${theme.spacing(8)};
  `);
const StyledActionsSection = styled.div(({
  theme
}) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};
    align-items: center;
  `);
const StyledFooter = styled.div(({
  theme
}) => css`
    padding: ${theme.spacing(8)};
    padding-bottom: ${theme.spacing(20)};
    text-align: center;
    display: flex;
    justify-content: center;
  `);

// Right panel - tan background with marketing content
const StyledRightPanel = styled.div(({
  theme
}) => css`
    background-color: ${theme.colors.surface.base.background};
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    position: relative;
    overflow: hidden;

    @media (max-width: ${({
  theme
}) => theme.breakpoints[1]}px) {
      display: none;
    }
  `);
const StyledRightContent = styled.div(({
  theme
}) => css`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: ${theme.spacing(8)};
    background: url('/images/half-pipes-${theme.mode}.svg') center center / cover no-repeat;
    gap: ${theme.spacing(6)};
  `);
const StyledMarketingCard = styled.div(({
  theme
}) => css`
    background-color: ${theme.colors.surface.base.surface};
    border-radius: 14px;
    box-shadow: ${theme.shadows.neutral.base};
    padding: ${theme.spacing(2)};
    width: 394px;
  `);
const StyledTabsContainer = styled.div(({
  theme
}) => css`
    background-color: ${theme.colors.surface.base.background};
    border: 1px solid ${theme.colors.outline.neutral};
    border-radius: ${theme.radius.normal};
    display: flex;
  `);
const StyledTab = styled.button<{
  active?: boolean;
}>(({
  theme,
  active
}) => css`
    flex: 1;
    padding: ${theme.spacing(1.5)} ${theme.spacing(3)};
    border: none;
    border-radius: ${theme.radius.normal};
    background-color: ${active ? theme.colors.surface.base.surface : 'transparent'};
    box-shadow: ${active ? theme.shadows.neutral.base : 'none'};
    font-family: 'JetBrains Mono', monospace;
    font-size: ${theme.pxToRem(14)};
    line-height: ${theme.pxToRem(20)};
    text-transform: uppercase;
    color: ${active ? theme.colors.on.neutral.primary : theme.colors.on.neutral.secondary_neutral};
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      color: ${theme.colors.on.neutral.primary};
    }
  `);
const StyledImageContainer = styled.div(({
  theme
}) => css`
    background-color: ${theme.colors.surface.base.background};
    border: 1px solid ${theme.colors.outline.neutral};
    border-radius: ${theme.radius.normal};
    height: 228px;
    margin-top: ${theme.spacing(2)};
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  `);
const StyledTrustedBySection = styled.div(({
  theme
}) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(6)};
    align-items: center;
    padding-top: ${theme.spacing(8)};
    position: absolute;
    bottom: ${theme.spacing(20)};
    left: 0;
    right: 0;
  `);
const StyledLogosGrid = styled.div(({
  theme
}) => css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(4)} ${theme.spacing(8)};
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    max-width: 640px;
  `);
const StyledLogoItem = styled.div(({
  theme
}) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 32px;
  `);
const StyledLogoMask = styled.span<{
  $src: string;
  $width: number;
  $height: number;
}>(({
  theme,
  $src,
  $width,
  $height
}) => css`
    display: inline-block;
    width: ${$width}px;
    height: ${$height}px;
    background-color: ${theme.mode === 'dark' ? '#99B4EBA3' : '#7A786E'};
    mask-image: url(${$src});
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;
    -webkit-mask-image: url(${$src});
    -webkit-mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
  `);
interface SignupLayoutProps {
  default_error?: string;
  token?: string;
  email?: string;
  redirect?: string;
  organization_slug?: string;
  promotion_slug?: string;
  referrer_slug?: string;
  product_source?: ProductSlug;
}
const SignupLayout: React.FC<SignupLayoutProps> = t0 => {
  const $ = _c(140);
  const {
    default_error,
    token,
    email,
    redirect,
    organization_slug,
    promotion_slug,
    referrer_slug,
    product_source: t1
  } = t0;
  const product_source = t1 === undefined ? PRODUCT_SLUGS.event_gateway : t1;
  const [error, updateError] = useState(default_error || null);
  const [sso, showSSO] = useState(false);
  const {
    HookdeckAPI,
    configs
  } = useContext(GlobalContext);
  const theme = useTheme();
  const [is_redirecting, setRedirecting] = useState(!!organization_slug);
  const [, setPromotion] = useLocalStorage(TrackingType.promotion, null);
  const [, setReferrer] = useLocalStorage(TrackingType.referrer, null);
  const auth_base_path = `${HookdeckAPI.api_url}/signup`;
  let query;
  let t2;
  if ($[0] !== product_source || $[1] !== redirect || $[2] !== token) {
    query = {};
    if (token) {
      query.invite_token = token;
    }
    if (redirect) {
      query.redirect = redirect;
    }
    query.product_source = product_source;
    t2 = Object.keys(query).length > 0 ? `?${stringifyQuery(query)}` : "";
    $[0] = product_source;
    $[1] = redirect;
    $[2] = token;
    $[3] = query;
    $[4] = t2;
  } else {
    query = $[3];
    t2 = $[4];
  }
  const querystring = t2;
  let t3;
  let t4;
  if ($[5] !== promotion_slug || $[6] !== setPromotion) {
    t3 = () => {
      if (promotion_slug) {
        setPromotion({
          slug: promotion_slug
        });
      }
    };
    t4 = [setPromotion, promotion_slug];
    $[5] = promotion_slug;
    $[6] = setPromotion;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[9] !== referrer_slug || $[10] !== setReferrer) {
    t5 = () => {
      if (referrer_slug) {
        setReferrer({
          slug: referrer_slug
        });
      }
    };
    t6 = [setReferrer, referrer_slug];
    $[9] = referrer_slug;
    $[10] = setReferrer;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] !== HookdeckAPI.auth || $[14] !== error || $[15] !== organization_slug || $[16] !== query) {
    t7 = () => {
      if (organization_slug) {
        HookdeckAPI.auth.getSSOUrl(undefined, organization_slug).then(t8 => {
          const {
            url
          } = t8;
          if (url) {
            window.location.href = `${url}?${query}`;
          }
        }).catch(e => {
          setRedirecting(false);
          updateError(error);
        });
      }
    };
    $[13] = HookdeckAPI.auth;
    $[14] = error;
    $[15] = organization_slug;
    $[16] = query;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== error || $[19] !== organization_slug || $[20] !== query) {
    t8 = [organization_slug, query, error];
    $[18] = error;
    $[19] = organization_slug;
    $[20] = query;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  useEffect(t7, t8);
  if (organization_slug && is_redirecting) {
    let t9;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <StyledSignupContainer><StyledLeftPanel><StyledFormContainer><Text muted={true} center={true}>Redirecting to your SSO provider...</Text></StyledFormContainer></StyledLeftPanel><StyledRightPanel /></StyledSignupContainer>;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    return t9;
  }
  if (sso || organization_slug) {
    const t9 = `/images/logo-colored-${theme.mode}.svg`;
    let t10;
    if ($[23] !== t9) {
      t10 = <StyledTopBar><a href={LINKS.landing}><img src={t9} height="20" alt="Hookdeck" /></a></StyledTopBar>;
      $[23] = t9;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    let t11;
    if ($[25] !== email || $[26] !== token) {
      t11 = email && token && <Alert inline={true} info={true} m={{
        b: 4
      }}>Sign up with{" "}<Text as="span" bold={true}>{email}</Text>{" "}to join the organization.</Alert>;
      $[25] = email;
      $[26] = token;
      $[27] = t11;
    } else {
      t11 = $[27];
    }
    const t12 = email ?? "";
    let t13;
    if ($[28] !== t12) {
      t13 = {
        email: t12
      };
      $[28] = t12;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    let t14;
    if ($[30] !== HookdeckAPI.auth || $[31] !== organization_slug || $[32] !== querystring) {
      t14 = async v => HookdeckAPI.auth.getSSOUrl(v.email, organization_slug).then(t15 => {
        const {
          url: url_0
        } = t15;
        return window.location.href = `${url_0}${querystring ? querystring.replace("?", "&") : ""}`;
      }).catch(e_0 => {
        let err = "Unexpected error";
        if (e_0.response?.message) {
          err = e_0.response?.message;
        } else {
          if (typeof e_0.response === "string") {
            err = e_0.response;
          }
        }
        updateError(err);
      });
      $[30] = HookdeckAPI.auth;
      $[31] = organization_slug;
      $[32] = querystring;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    let t15;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <Text bold={true} size="2xl" m={{
        b: 3
      }}>Sign up to continue</Text>;
      $[34] = t15;
    } else {
      t15 = $[34];
    }
    let t16;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = {
        b: 5
      };
      $[35] = t16;
    } else {
      t16 = $[35];
    }
    let t17;
    if ($[36] !== organization_slug) {
      t17 = !organization_slug && <><br /><br />If you are unsure whether your organization supports Single Sign-On, please contact your administrator.</>;
      $[36] = organization_slug;
      $[37] = t17;
    } else {
      t17 = $[37];
    }
    let t18;
    if ($[38] !== t17) {
      t18 = <Text as="p" m={t16}>Single Sign-On allows you to connect to Hookdeck using your organization's identity provider.{t17}</Text>;
      $[38] = t17;
      $[39] = t18;
    } else {
      t18 = $[39];
    }
    let t19;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <TextInput label="Work Email" name="email" auto_complete="email" type="email" placeholder="you@example.com" required={true} />;
      $[40] = t19;
    } else {
      t19 = $[40];
    }
    let t20;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <Button submit={true} large={true} m={{
        t: 2
      }} primary={true} block={true}>Single Sign-On</Button>;
      $[41] = t20;
    } else {
      t20 = $[41];
    }
    let t21;
    if ($[42] !== default_error || $[43] !== error) {
      t21 = (error || default_error) && <Alert inline={true} danger={true} m={{
        t: 3
      }}>{error || default_error}</Alert>;
      $[42] = default_error;
      $[43] = error;
      $[44] = t21;
    } else {
      t21 = $[44];
    }
    let t22;
    if ($[45] !== organization_slug) {
      t22 = !organization_slug && <Text center={true} muted={true} m={{
        t: 4
      }}><Link neutral={true} no_underline={true} onClick={() => showSSO(false)}>Cancel</Link></Text>;
      $[45] = organization_slug;
      $[46] = t22;
    } else {
      t22 = $[46];
    }
    let t23;
    if ($[47] !== t18 || $[48] !== t21 || $[49] !== t22) {
      t23 = <Form>{t15}{t18}{t19}{t20}{t21}{t22}</Form>;
      $[47] = t18;
      $[48] = t21;
      $[49] = t22;
      $[50] = t23;
    } else {
      t23 = $[50];
    }
    let t24;
    if ($[51] !== t13 || $[52] !== t14 || $[53] !== t23) {
      t24 = <Formik initialValues={t13} validate={_temp} onSubmit={t14}>{t23}</Formik>;
      $[51] = t13;
      $[52] = t14;
      $[53] = t23;
      $[54] = t24;
    } else {
      t24 = $[54];
    }
    let t25;
    if ($[55] !== t11 || $[56] !== t24) {
      t25 = <StyledFormContainer><StyledFormContent>{t11}{t24}</StyledFormContent></StyledFormContainer>;
      $[55] = t11;
      $[56] = t24;
      $[57] = t25;
    } else {
      t25 = $[57];
    }
    let t26;
    if ($[58] !== t10 || $[59] !== t25) {
      t26 = <StyledLeftPanel>{t10}{t25}</StyledLeftPanel>;
      $[58] = t10;
      $[59] = t25;
      $[60] = t26;
    } else {
      t26 = $[60];
    }
    let t27;
    if ($[61] !== product_source) {
      t27 = <StyledRightPanel><RightPanelContent product_source={product_source} /></StyledRightPanel>;
      $[61] = product_source;
      $[62] = t27;
    } else {
      t27 = $[62];
    }
    let t28;
    if ($[63] !== t26 || $[64] !== t27) {
      t28 = <StyledSignupContainer>{t26}{t27}</StyledSignupContainer>;
      $[63] = t26;
      $[64] = t27;
      $[65] = t28;
    } else {
      t28 = $[65];
    }
    return t28;
  }
  let t9;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <AuthSensitivePageHead />;
    $[66] = t9;
  } else {
    t9 = $[66];
  }
  const t10 = `/images/logo-colored-${theme.mode}.svg`;
  let t11;
  if ($[67] !== t10) {
    t11 = <StyledTopBar><a href={LINKS.landing}><img src={t10} height="20" alt="Hookdeck" /></a></StyledTopBar>;
    $[67] = t10;
    $[68] = t11;
  } else {
    t11 = $[68];
  }
  let t12;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Text bold={true} size="2xl" style={{
      textAlign: "center"
    }}>Create your Hookdeck account</Text>;
    $[69] = t12;
  } else {
    t12 = $[69];
  }
  let t13;
  if ($[70] !== email || $[71] !== token) {
    t13 = email && token && <Alert inline={true} info={true}>Please sign up with{" "}<Text as="span" bold={true}>{email}</Text>{" "}to join the organization.</Alert>;
    $[70] = email;
    $[71] = token;
    $[72] = t13;
  } else {
    t13 = $[72];
  }
  let t14;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      y: 1.5,
      x: 3
    };
    $[73] = t14;
  } else {
    t14 = $[73];
  }
  const t15 = `${auth_base_path}/github${querystring}`;
  let t16;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Icon icon="github" left={true} />;
    $[74] = t16;
  } else {
    t16 = $[74];
  }
  let t17;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Text as="span">Sign up with <strong>Github</strong></Text>;
    $[75] = t17;
  } else {
    t17 = $[75];
  }
  let t18;
  if ($[76] !== t15) {
    t18 = <Button outline={true} as="a" p={t14} href={t15}>{t16}{t17}</Button>;
    $[76] = t15;
    $[77] = t18;
  } else {
    t18 = $[77];
  }
  let t19;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      y: 1.5,
      x: 3
    };
    $[78] = t19;
  } else {
    t19 = $[78];
  }
  const t20 = `${auth_base_path}/google${querystring}`;
  const t21 = `${configs.APP_URL}/images/google-icon.png`;
  let t22;
  if ($[79] !== t21) {
    t22 = <Icon src={t21} left={true} />;
    $[79] = t21;
    $[80] = t22;
  } else {
    t22 = $[80];
  }
  let t23;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Text as="span">Sign up with <strong>Google</strong></Text>;
    $[81] = t23;
  } else {
    t23 = $[81];
  }
  let t24;
  if ($[82] !== t20 || $[83] !== t22) {
    t24 = <Button outline={true} as="a" p={t19} href={t20}>{t22}{t23}</Button>;
    $[82] = t20;
    $[83] = t22;
    $[84] = t24;
  } else {
    t24 = $[84];
  }
  let t25;
  let t26;
  let t27;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => showSSO(true);
    t26 = {
      y: 1.5,
      x: 3
    };
    t27 = <Icon icon="key" left={true} />;
    $[85] = t25;
    $[86] = t26;
    $[87] = t27;
  } else {
    t25 = $[85];
    t26 = $[86];
    t27 = $[87];
  }
  let t28;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <Button outline={true} onClick={t25} p={t26}>{t27}<Text as="span">Sign up with <strong>SSO</strong></Text></Button>;
    $[88] = t28;
  } else {
    t28 = $[88];
  }
  let t29;
  if ($[89] !== t18 || $[90] !== t24) {
    t29 = <StyledOAuthSection>{t18}{t24}{t28}</StyledOAuthSection>;
    $[89] = t18;
    $[90] = t24;
    $[91] = t29;
  } else {
    t29 = $[91];
  }
  let t30;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Divider m={{
      t: 10,
      b: 8
    }} />;
    $[92] = t30;
  } else {
    t30 = $[92];
  }
  const t31 = email ?? "";
  let t32;
  if ($[93] !== t31) {
    t32 = {
      email: t31,
      password: "",
      name: ""
    };
    $[93] = t31;
    $[94] = t32;
  } else {
    t32 = $[94];
  }
  let t33;
  if ($[95] !== HookdeckAPI || $[96] !== configs.ENV || $[97] !== product_source || $[98] !== query) {
    t33 = v_0 => {
      HookdeckAPI.auth.signup({
        email: v_0.email,
        password: v_0.password,
        name: v_0.name,
        product_source
      }, query).then(async r => {
        if (isMultiFactorRequired(r)) {
          (window as any).location = `/signin/mfa?token=${r.multi_factor_token}`;
        } else {
          if (r.redirect) {
            await completeAuthRedirect(HookdeckAPI, configs.ENV, r.redirect);
          } else {
            await completeAuthRedirect(HookdeckAPI, configs.ENV);
          }
        }
      }).catch(e_1 => {
        let err_0 = "Unexpected error";
        if (e_1.response?.message) {
          err_0 = e_1.response?.message;
        } else {
          if (typeof e_1.response === "string") {
            err_0 = e_1.response;
          } else {
            if (e_1.response?.data?.[0]) {
              err_0 = capitalizeFirstLetter(e_1.response?.data?.[0]);
            }
          }
        }
        updateError(err_0);
      });
    };
    $[95] = HookdeckAPI;
    $[96] = configs.ENV;
    $[97] = product_source;
    $[98] = query;
    $[99] = t33;
  } else {
    t33 = $[99];
  }
  let t34;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <StyledFormSection><TextInput label="Name" name="name" auto_complete="name" type="name" placeholder="Your name" /><TextInput label="Email" name="email" auto_complete="email" type="email" placeholder="you@example.com" /><TextInput type="password" label="Password" name="password" auto_complete="new-password" maxlength={255} minlength={8} /></StyledFormSection>;
    $[100] = t34;
  } else {
    t34 = $[100];
  }
  let t35;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <Button submit={true} large={true} primary={true} block={true}>Create account</Button>;
    $[101] = t35;
  } else {
    t35 = $[101];
  }
  let t36;
  if ($[102] !== default_error || $[103] !== error) {
    t36 = (error || default_error) && <Alert inline={true} danger={true} style={{
      width: "100%"
    }}>{error || default_error}</Alert>;
    $[102] = default_error;
    $[103] = error;
    $[104] = t36;
  } else {
    t36 = $[104];
  }
  const t37 = `/signin${querystring}`;
  let t38;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <Link>Sign in</Link>;
    $[105] = t38;
  } else {
    t38 = $[105];
  }
  let t39;
  if ($[106] !== t37) {
    t39 = <Text muted={true}>Already have an account?{" "}<NextLink legacyBehavior={true} passHref={true} href={t37}>{t38}</NextLink></Text>;
    $[106] = t37;
    $[107] = t39;
  } else {
    t39 = $[107];
  }
  let t40;
  if ($[108] !== t36 || $[109] !== t39) {
    t40 = <Form>{t34}<StyledActionsSection>{t35}{t36}{t39}</StyledActionsSection></Form>;
    $[108] = t36;
    $[109] = t39;
    $[110] = t40;
  } else {
    t40 = $[110];
  }
  let t41;
  if ($[111] !== t32 || $[112] !== t33 || $[113] !== t40) {
    t41 = <Formik initialValues={t32} validate={_temp2} onSubmit={t33}>{t40}</Formik>;
    $[111] = t32;
    $[112] = t33;
    $[113] = t40;
    $[114] = t41;
  } else {
    t41 = $[114];
  }
  let t42;
  if ($[115] !== t29 || $[116] !== t41) {
    t42 = <Div>{t29}{t30}{t41}</Div>;
    $[115] = t29;
    $[116] = t41;
    $[117] = t42;
  } else {
    t42 = $[117];
  }
  let t43;
  if ($[118] !== t13 || $[119] !== t42) {
    t43 = <StyledFormContainer><StyledFormContent>{t12}{t13}{t42}</StyledFormContent></StyledFormContainer>;
    $[118] = t13;
    $[119] = t42;
    $[120] = t43;
  } else {
    t43 = $[120];
  }
  let t44;
  if ($[121] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = {
      px: 300
    };
    $[121] = t44;
  } else {
    t44 = $[121];
  }
  let t45;
  if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <Text as="span" neutral={true} underline={true}>Terms of Service</Text>;
    $[122] = t45;
  } else {
    t45 = $[122];
  }
  let t46;
  if ($[123] !== theme.font_weights.regular) {
    t46 = <Link font_weight={theme.font_weights.regular} href={LINKS.terms}>{t45}</Link>;
    $[123] = theme.font_weights.regular;
    $[124] = t46;
  } else {
    t46 = $[124];
  }
  let t47;
  if ($[125] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <Text as="span" neutral={true} underline={true}>Privacy Policies</Text>;
    $[125] = t47;
  } else {
    t47 = $[125];
  }
  let t48;
  if ($[126] !== theme.font_weights.regular) {
    t48 = <Link font_weight={theme.font_weights.regular} href={LINKS.privacy}>{t47}</Link>;
    $[126] = theme.font_weights.regular;
    $[127] = t48;
  } else {
    t48 = $[127];
  }
  let t49;
  if ($[128] !== t46 || $[129] !== t48) {
    t49 = <StyledFooter><Text w={t44} neutral={true}>By signing up, you agree to our{" "}{t46}{" "}and{" "}{t48}</Text></StyledFooter>;
    $[128] = t46;
    $[129] = t48;
    $[130] = t49;
  } else {
    t49 = $[130];
  }
  let t50;
  if ($[131] !== t11 || $[132] !== t43 || $[133] !== t49) {
    t50 = <StyledLeftPanel>{t11}{t43}{t49}</StyledLeftPanel>;
    $[131] = t11;
    $[132] = t43;
    $[133] = t49;
    $[134] = t50;
  } else {
    t50 = $[134];
  }
  let t51;
  if ($[135] !== product_source) {
    t51 = <StyledRightPanel><RightPanelContent product_source={product_source} /></StyledRightPanel>;
    $[135] = product_source;
    $[136] = t51;
  } else {
    t51 = $[136];
  }
  let t52;
  if ($[137] !== t50 || $[138] !== t51) {
    t52 = <StyledSignupContainer>{t9}{t50}{t51}</StyledSignupContainer>;
    $[137] = t50;
    $[138] = t51;
    $[139] = t52;
  } else {
    t52 = $[139];
  }
  return t52;
};

// Right panel content component
const RightPanelContent = t0 => {
  const $ = _c(23);
  const {
    product_source
  } = t0;
  const theme = useTheme();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["receive", "send", "connect", "test"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const tabs = t1 as const;
  const [active_tab, setActiveTab] = useState(product_source === PRODUCT_SLUGS.outpost ? tabs[1] : tabs[0]);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      px: 408
    };
    t3 = {
      lineHeight: "24px"
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Text muted={true} center={true} w={t2} style={t3}>Everything you need to work with webhooks and external events<Text as="span" dark={true}>—without building it yourself</Text></Text>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== active_tab) {
    t5 = <StyledTabsContainer>{tabs.map(tab => <StyledTab key={tab} active={active_tab === tab} onClick={() => setActiveTab(tab)}>{tab}</StyledTab>)}</StyledTabsContainer>;
    $[4] = active_tab;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = `/images/${active_tab}-signup-preview-${theme.mode}.png`;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      maxWidth: "100%",
      maxHeight: "100%"
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t6) {
    t8 = <StyledImageContainer><img src={t6} alt="Hookdeck workflow" style={t7} /></StyledImageContainer>;
    $[7] = t6;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t5 || $[10] !== t8) {
    t9 = <StyledRightContent>{t4}<StyledMarketingCard>{t5}{t8}</StyledMarketingCard></StyledRightContent>;
    $[9] = t5;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Text size="s" muted={true} style={{
      fontFamily: "'JetBrains Mono', monospace",
      letterSpacing: "0.5px"
    }}>TRUSTED BY</Text>;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <StyledLogoItem><StyledLogoMask $src="/images/trusted-by-companies/lemonsqueezy-logo.svg" $width={159} $height={32} role="img" aria-label="Lemon Squeezy" /></StyledLogoItem>;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <StyledLogoItem><StyledLogoMask $src="/images/trusted-by-companies/crunchtime-logo.svg" $width={135} $height={32} role="img" aria-label="CrunchTime" /></StyledLogoItem>;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <StyledLogoItem><StyledLogoMask $src="/images/trusted-by-companies/chartmogul-logo.svg" $width={125} $height={32} role="img" aria-label="ChartMogul" /></StyledLogoItem>;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <StyledLogoItem><StyledLogoMask $src="/images/trusted-by-companies/arcade-logo.svg" $width={125} $height={32} role="img" aria-label="Arcade" /></StyledLogoItem>;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <StyledLogoItem><StyledLogoMask $src="/images/trusted-by-companies/bolt-logo.svg" $width={84} $height={32} role="img" aria-label="Bolt" /></StyledLogoItem>;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <StyledLogoItem><StyledLogoMask $src="/images/trusted-by-companies/padlet-logo.svg" $width={84} $height={32} role="img" aria-label="Padlet" /></StyledLogoItem>;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  let t17;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <StyledLogoItem><StyledLogoMask $src="/images/trusted-by-companies/gorgias-logo.svg" $width={107} $height={32} role="img" aria-label="Gorgias" /></StyledLogoItem>;
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  let t18;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <StyledTrustedBySection>{t10}<StyledLogosGrid>{t11}{t12}{t13}{t14}{t15}{t16}{t17}<StyledLogoItem><StyledLogoMask $src="/images/trusted-by-companies/gemini-logo.svg" $width={107} $height={32} role="img" aria-label="Gemini" /></StyledLogoItem></StyledLogosGrid></StyledTrustedBySection>;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  let t19;
  if ($[21] !== t9) {
    t19 = <>{t9}{t18}</>;
    $[21] = t9;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  return t19;
};
export default SignupLayout;
function _temp(values) {
  const errors = {};
  if (!values.email || values.email.length === 0) {
    errors.email = "Required";
  }
  return errors;
}
function _temp2(values_0) {
  const errors_0 = {};
  if (!values_0.email || values_0.email.length === 0) {
    errors_0.email = "Required";
  }
  if (!values_0.password || values_0.password.length === 0) {
    errors_0.password = "Required";
  } else {
    if (values_0.password.length < 8) {
      errors_0.password = "Password must contain at least 8 characters";
    }
  }
  return errors_0;
}